import type { Language } from "@/i18n/translations";

// Route mapping between PT-BR, EN, and ES
export const routeMap: Record<string, Record<Language, string>> = {
  home: { 'pt-BR': '/', 'en': '/', 'es': '/' },

  // Auth
  login: { 'pt-BR': '/login', 'en': '/login', 'es': '/iniciar-sesion' },
  register: { 'pt-BR': '/cadastrar', 'en': '/register', 'es': '/registrarse' },

  // Courses
  coursesLanding: { 'pt-BR': '/cursos', 'en': '/courses', 'es': '/cursos' },
  catalog: { 'pt-BR': '/catalogo', 'en': '/catalog', 'es': '/catalogo' },
  explore: { 'pt-BR': '/explorar', 'en': '/explore', 'es': '/explorar' },

  // Categories
  categories: { 'pt-BR': '/categorias', 'en': '/categories', 'es': '/categorias' },
  quality: { 'pt-BR': '/qualidade', 'en': '/quality', 'es': '/calidad' },
  environment: { 'pt-BR': '/meio-ambiente', 'en': '/environment', 'es': '/medio-ambiente' },
  workSafety: { 'pt-BR': '/seguranca-do-trabalho', 'en': '/occupational-health-safety', 'es': '/seguridad-laboral' },
  infoSecurity: { 'pt-BR': '/seguranca-da-informacao', 'en': '/information-security', 'es': '/seguridad-de-la-informacion' },
  automotive: { 'pt-BR': '/automotivo', 'en': '/automotive', 'es': '/automotriz' },

  // Student Area
  myCourses: { 'pt-BR': '/meus-cursos', 'en': '/my-courses', 'es': '/mis-cursos' },
  profile: { 'pt-BR': '/perfil', 'en': '/profile', 'es': '/perfil' },
  certificates: { 'pt-BR': '/certificados', 'en': '/certificates', 'es': '/certificados' },

  // Course dynamic routes
  courseDetail: { 'pt-BR': '/curso', 'en': '/course', 'es': '/curso' },
  coursePlayer: { 'pt-BR': '/aprender', 'en': '/learn', 'es': '/aprender' },
  checkout: { 'pt-BR': '/checkout', 'en': '/checkout', 'es': '/checkout' },

  // Corporate
  companies: { 'pt-BR': '/empresas', 'en': '/companies', 'es': '/empresas' },
  corporateSolutions: { 'pt-BR': '/solucoes-corporativas', 'en': '/corporate-solutions', 'es': '/soluciones-corporativas' },

  // Institutional
  about: { 'pt-BR': '/sobre', 'en': '/about', 'es': '/acerca-de' },
  faq: { 'pt-BR': '/faq', 'en': '/faq', 'es': '/faq' },
  contact: { 'pt-BR': '/contato', 'en': '/contact', 'es': '/contacto' },

  // Legal
  termsOfUse: { 'pt-BR': '/termos-de-uso', 'en': '/terms-of-use', 'es': '/terminos-de-uso' },
  privacyPolicy: { 'pt-BR': '/politica-de-privacidade', 'en': '/privacy-policy', 'es': '/politica-de-privacidad' },
  cookiePolicy: { 'pt-BR': '/politica-de-cookies', 'en': '/cookie-policy', 'es': '/politica-de-cookies' },

  // Password recovery
  recoverPassword: { 'pt-BR': '/recuperar-senha', 'en': '/recover-password', 'es': '/recuperar-contrasena' },
  resetPassword: { 'pt-BR': '/redefinir-senha', 'en': '/reset-password', 'es': '/restablecer-contrasena' },

  // Certificate verification
  verifyCertificate: { 'pt-BR': '/verificar', 'en': '/verify', 'es': '/verificar' },
};

// Get route for a specific key and language
export function getRoute(key: string, language: Language): string {
  const route = routeMap[key];
  if (route) {
    return route[language];
  }
  return '/';
}

// Find route key from path (for any language)
export function findRouteKey(path: string): string | null {
  const cleanPath = path.split('?')[0].split('#')[0];

  const allLangs: Language[] = ['pt-BR', 'en', 'es'];

  for (const [key, routes] of Object.entries(routeMap)) {
    for (const lang of allLangs) {
      if (routes[lang] === cleanPath) {
        return key;
      }
    }
  }

  // Check for dynamic routes
  const dynamicRoutes = ['courseDetail', 'coursePlayer', 'checkout', 'verifyCertificate'];
  for (const key of dynamicRoutes) {
    const routes = routeMap[key];
    for (const lang of allLangs) {
      if (cleanPath.startsWith(routes[lang] + '/')) {
        return key;
      }
    }
  }

  return null;
}

// Get the equivalent route in another language
export function translateRoute(currentPath: string, fromLang: Language, toLang: Language): string {
  const [pathPart, ...rest] = currentPath.split('?');
  const queryPart = rest.length > 0 ? '?' + rest.join('?') : '';

  // Check for dynamic route patterns
  const dynamicPatterns = [
    { key: 'courseDetail', prefixes: { 'pt-BR': '/curso/', 'en': '/course/', 'es': '/curso/' } },
    { key: 'coursePlayer', prefixes: { 'pt-BR': '/aprender/', 'en': '/learn/', 'es': '/aprender/' } },
    { key: 'checkout', prefixes: { 'pt-BR': '/checkout/', 'en': '/checkout/', 'es': '/checkout/' } },
    { key: 'categories', prefixes: { 'pt-BR': '/categorias/', 'en': '/categories/', 'es': '/categorias/' } },
    { key: 'verifyCertificate', prefixes: { 'pt-BR': '/verificar/', 'en': '/verify/', 'es': '/verificar/' } },
  ];

  for (const pattern of dynamicPatterns) {
    const fromPrefix = pattern.prefixes[fromLang];
    const toPrefix = pattern.prefixes[toLang];

    if (pathPart.startsWith(fromPrefix)) {
      const slug = pathPart.replace(fromPrefix, '');
      return toPrefix + slug + queryPart;
    }
  }

  // Static routes
  for (const routes of Object.values(routeMap)) {
    if (routes[fromLang] === pathPart) {
      return routes[toLang] + queryPart;
    }
  }

  return currentPath;
}

// Get all routes for a specific language
export function getAllRoutes(language: Language): string[] {
  return Object.values(routeMap).map(routes => routes[language]);
}

// Category routes mapping for navigation
export const categoryRoutes: Record<string, { key: string; nameKey: string }> = {
  '/qualidade': { key: 'quality', nameKey: 'quality' },
  '/quality': { key: 'quality', nameKey: 'quality' },
  '/calidad': { key: 'quality', nameKey: 'quality' },
  '/meio-ambiente': { key: 'environment', nameKey: 'environment' },
  '/environment': { key: 'environment', nameKey: 'environment' },
  '/medio-ambiente': { key: 'environment', nameKey: 'environment' },
  '/seguranca-do-trabalho': { key: 'workSafety', nameKey: 'workSafety' },
  '/occupational-health-safety': { key: 'workSafety', nameKey: 'workSafety' },
  '/seguridad-laboral': { key: 'workSafety', nameKey: 'workSafety' },
  '/seguranca-da-informacao': { key: 'infoSecurity', nameKey: 'infoSecurity' },
  '/information-security': { key: 'infoSecurity', nameKey: 'infoSecurity' },
  '/seguridad-de-la-informacion': { key: 'infoSecurity', nameKey: 'infoSecurity' },
  '/automotivo': { key: 'automotive', nameKey: 'automotive' },
  '/automotive': { key: 'automotive', nameKey: 'automotive' },
  '/automotriz': { key: 'automotive', nameKey: 'automotive' },
};
